import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
    FaCalendarCheck,
    FaCalendarTimes,
    FaClock,
    FaSignInAlt,
    FaSignOutAlt,
    FaChevronRight,
    FaCalendarAlt,
} from 'react-icons/fa';
import { formatDate, formatTime, formatJamKerja, formatNumber } from '../../helper/helper';
import AttendanceDetailModal from '../../component/Panel/AttendanceDetailModal';

const Dashboard = ({ data }) => {
    const [absensiHariIni, setAbsensiHariIni] = useState(null);
    const [absensiBulanIni, setAbsensiBulanIni] = useState([]);
    const [loading, setLoading] = useState(true);
    const [monthSummary, setMonthSummary] = useState(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [modalData, setModalData] = useState(null);

    const getDashboardData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/absensi/dashboard`, {
                withCredentials: true,
            });
            const dashboardData = response.data;
            setAbsensiHariIni(dashboardData.absensiHariIni || null);
            setAbsensiBulanIni(dashboardData.absensiTerakhir || []);
            setMonthSummary(dashboardData.month || null);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getDashboardData();
    }, []);

    const openModal = (item) => {
        setModalData(item);
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setModalData(null);
    };

    const period = monthSummary?.period
        || `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
    const monthLabel = new Date(`${period}-01`).toLocaleDateString('id-ID', {
        month: 'long',
        year: 'numeric',
    });

    const hasMasuk = !!absensiHariIni?.waktuMasuk;
    const hasPulang = !!absensiHariIni?.waktuKeluar;
    const todayComplete = hasMasuk && hasPulang;

    return (
        <div className="mb-32 min-h-[calc(100vh-80px)] py-5">
            <div className="mx-auto max-w-7xl px-2 md:px-4">
                {/* Header — full width, sama pola panel */}
                <div className="mb-6 flex flex-col items-start justify-between gap-4 overflow-hidden rounded-zafu-xl bg-gradient-to-br from-zafu-green to-zafu-green-soft p-6 text-white shadow-zafu-lg md:flex-row md:items-center md:p-8">
                    <div>
                        <p className="mb-1 text-xs uppercase tracking-[0.2em] text-white/70">
                            Zafu Residence
                        </p>
                        <h1 className="font-display text-3xl font-semibold md:text-4xl">Dashboard</h1>
                        <p className="mt-2 text-sm text-white/80">
                            Selamat datang, {data.user.nama}
                        </p>
                    </div>
                    <div className="text-left md:text-right">
                        <p className="text-xs uppercase tracking-wider text-white/60">Devisi</p>
                        <p className="font-medium text-white">{data.user.nama_devisi || '-'}</p>
                    </div>
                </div>

                {/* Target bulan — strip ringkas, brand only */}
                {monthSummary && (
                    <div className="mb-6">
                        <div className="mb-3 flex items-center justify-between gap-2">
                            <h2 className="text-base font-semibold text-zafu-ink">Target Jam Kerja</h2>
                            <div className="flex items-center gap-2 text-sm text-zafu-muted">
                                <FaCalendarAlt className="text-zafu-green" />
                                <span>{monthLabel}</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                            <div className="rounded-xl border border-zafu-cream-dark/60 bg-white px-3 py-3">
                                <p className="text-[10px] text-zafu-muted">Target / hari</p>
                                <p className="mt-0.5 text-lg font-bold text-zafu-green">
                                    {formatNumber(monthSummary.standard_jam_kerja || 7)} jam
                                </p>
                            </div>
                            <div className="rounded-xl border border-zafu-cream-dark/60 bg-white px-3 py-3">
                                <p className="text-[10px] text-zafu-muted">Hari kerja</p>
                                <p className="mt-0.5 text-lg font-bold text-zafu-green">
                                    {monthSummary.totalHariKerja ?? 0} hari
                                </p>
                            </div>
                            <div className="rounded-xl border border-zafu-cream-dark/60 bg-white px-3 py-3">
                                <p className="text-[10px] text-zafu-muted">Hadir</p>
                                <p className="mt-0.5 text-lg font-bold text-zafu-ink">
                                    {monthSummary.totalHadir ?? 0}
                                </p>
                            </div>
                            <div className="rounded-xl border border-zafu-cream-dark/60 bg-white px-3 py-3">
                                <p className="text-[10px] text-zafu-muted">Total jam</p>
                                <p className="mt-0.5 text-lg font-bold text-zafu-ink">
                                    {formatNumber(monthSummary.totalJamKerja ?? 0)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* 2 kolom: hari ini + terakhir */}
                <div className="grid gap-6 md:grid-cols-2">
                    {/* Absensi Hari Ini */}
                    <div className="rounded-zafu-xl border border-zafu-cream-dark/60 bg-white p-5 shadow-zafu md:p-6">
                        <div className="mb-4 flex items-center justify-between gap-2">
                            <h2 className="flex items-center gap-2 text-lg font-semibold text-zafu-ink">
                                <FaClock className="text-zafu-green" />
                                Absensi Hari Ini
                            </h2>
                            {!todayComplete && (
                                <Link
                                    to="/absensi"
                                    className="rounded-lg bg-zafu-green px-3 py-1.5 text-xs font-semibold text-white hover:bg-zafu-green-light"
                                >
                                    Absen
                                </Link>
                            )}
                        </div>

                        {loading ? (
                            <div className="space-y-3">
                                <div className="h-16 animate-pulse rounded-xl bg-zafu-cream" />
                                <div className="h-16 animate-pulse rounded-xl bg-zafu-cream" />
                            </div>
                        ) : absensiHariIni && (hasMasuk || hasPulang) ? (
                            <div className="space-y-3">
                                {hasMasuk && (
                                    <div className="flex items-center gap-3 rounded-xl bg-zafu-cream/80 px-4 py-3">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zafu-green/10 text-zafu-green">
                                            <FaSignInAlt />
                                        </div>
                                        <div>
                                            <p className="text-xs text-zafu-muted">Masuk</p>
                                            <p className="text-lg font-semibold tabular-nums text-zafu-ink">
                                                {formatTime(absensiHariIni.waktuMasuk)}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {hasPulang && (
                                    <div className="flex items-center gap-3 rounded-xl bg-zafu-cream/80 px-4 py-3">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zafu-green/10 text-zafu-green">
                                            <FaSignOutAlt />
                                        </div>
                                        <div>
                                            <p className="text-xs text-zafu-muted">Pulang</p>
                                            <p className="text-lg font-semibold tabular-nums text-zafu-ink">
                                                {formatTime(absensiHariIni.waktuKeluar)}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {todayComplete && absensiHariIni.jumlahJamKerja && (
                                    <div className="flex items-center gap-3 rounded-xl border border-zafu-green/15 bg-zafu-green/[0.06] px-4 py-3">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zafu-green text-white">
                                            <FaClock />
                                        </div>
                                        <div>
                                            <p className="text-xs text-zafu-muted">Jam kerja</p>
                                            <p className="text-lg font-semibold text-zafu-green">
                                                {formatJamKerja(absensiHariIni.jumlahJamKerja)}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {hasMasuk && !hasPulang && (
                                    <p className="pt-1 text-center text-sm text-zafu-muted">
                                        Belum absen pulang
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="py-10 text-center text-zafu-muted">
                                <FaCalendarTimes className="mx-auto mb-2 text-3xl text-zafu-muted/40" />
                                <p className="text-sm">Belum ada absensi hari ini</p>
                                <Link
                                    to="/absensi"
                                    className="mt-3 inline-block text-sm font-medium text-zafu-green hover:underline"
                                >
                                    Absen sekarang
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Absensi Terakhir */}
                    <div className="rounded-zafu-xl border border-zafu-cream-dark/60 bg-white p-5 shadow-zafu md:p-6">
                        <div className="mb-4 flex items-center justify-between gap-2">
                            <h2 className="flex items-center gap-2 text-lg font-semibold text-zafu-ink">
                                <FaCalendarCheck className="text-zafu-green" />
                                Absensi Terakhir
                            </h2>
                            <Link
                                to="/report"
                                className="text-xs font-medium text-zafu-green hover:underline"
                            >
                                Lihat report
                            </Link>
                        </div>

                        {loading ? (
                            <div className="space-y-3">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="h-14 animate-pulse rounded-xl bg-zafu-cream" />
                                ))}
                            </div>
                        ) : absensiBulanIni.length > 0 ? (
                            <ul className="divide-y divide-zafu-cream-dark/70">
                                {absensiBulanIni.map((item, index) => {
                                    const complete = item.waktuMasuk && item.waktuKeluar;
                                    return (
                                        <li key={item.idAbsensi || item.id_absensi || index}>
                                            <button
                                                type="button"
                                                onClick={() => openModal(item)}
                                                className="flex w-full items-center gap-3 py-3 text-left transition hover:bg-zafu-cream/40 -mx-1 px-1 rounded-lg"
                                            >
                                                <span
                                                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                                                        complete
                                                            ? 'bg-zafu-green/10 text-zafu-green'
                                                            : item.status
                                                                ? 'bg-amber-50 text-amber-700'
                                                                : 'bg-zafu-cream text-zafu-muted'
                                                    }`}
                                                >
                                                    {complete ? (
                                                        <FaCalendarCheck className="text-sm" />
                                                    ) : (
                                                        <FaCalendarTimes className="text-sm" />
                                                    )}
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="block truncate text-sm font-medium text-zafu-ink">
                                                        {formatDate(item.tanggal)}
                                                    </span>
                                                    <span className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-zafu-muted">
                                                        {item.waktuMasuk && (
                                                            <span>Masuk {formatTime(item.waktuMasuk)}</span>
                                                        )}
                                                        {item.waktuKeluar && (
                                                            <span>Pulang {formatTime(item.waktuKeluar)}</span>
                                                        )}
                                                        {item.status && <span>{item.status}</span>}
                                                        {item.jumlahJamKerja && (
                                                            <span className="text-zafu-green">
                                                                {formatJamKerja(item.jumlahJamKerja)}
                                                            </span>
                                                        )}
                                                    </span>
                                                </span>
                                                <FaChevronRight className="shrink-0 text-[10px] text-zafu-muted/40" />
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <div className="py-10 text-center text-zafu-muted">
                                <FaCalendarTimes className="mx-auto mb-2 text-3xl text-zafu-muted/40" />
                                <p className="text-sm">Belum ada data absensi</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <AttendanceDetailModal
                isOpen={modalIsOpen}
                onClose={closeModal}
                items={modalData}
            />
        </div>
    );
};

export default Dashboard;
